import type { ReactNode } from 'react'
import * as RadioGroupPrimitive from '@radix-ui/react-radio-group'
import { cn } from '@/lib/utils'

export interface RadioOption {
  value: string
  label: ReactNode
  /** Supporting line under the label. */
  description?: ReactNode
}

/**
 * Ledger radio group over Radix. Two shapes:
 * - `card` — the bordered pick-cards from the purchasing wizard's ship-to
 *   picker, generalized (two-up grid above `sm`).
 * - `stacked` — a plain dot-and-label column for question forms.
 * Label the group itself via `aria-label` or `aria-labelledby`.
 */
export function RadioGroup({
  value,
  onValueChange,
  options,
  variant = 'stacked',
  className,
  'aria-label': ariaLabel,
  'aria-labelledby': ariaLabelledby,
}: {
  value: string | null
  onValueChange: (value: string) => void
  options: RadioOption[]
  variant?: 'stacked' | 'card'
  className?: string
  'aria-label'?: string
  'aria-labelledby'?: string
}) {
  return (
    <RadioGroupPrimitive.Root
      value={value ?? ''}
      onValueChange={onValueChange}
      aria-label={ariaLabel}
      aria-labelledby={ariaLabelledby}
      className={cn(variant === 'card' ? 'grid gap-3 sm:grid-cols-2' : 'grid gap-1', className)}
    >
      {options.map((option) =>
        variant === 'card' ? (
          <RadioGroupPrimitive.Item
            key={option.value}
            value={option.value}
            className={cn(
              'rounded-lg border bg-card px-4 py-3.5 text-left transition-colors duration-[180ms] ease-editorial',
              'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand',
              'data-[state=checked]:border-brand data-[state=checked]:bg-brandTint/40',
              'data-[state=unchecked]:border-rule/80 data-[state=unchecked]:hover:border-mute data-[state=unchecked]:hover:bg-cream',
            )}
          >
            <span className="block text-sm font-medium text-ink">{option.label}</span>
            {option.description ? (
              <span className="mt-0.5 block text-xs text-mute">{option.description}</span>
            ) : null}
          </RadioGroupPrimitive.Item>
        ) : (
          <RadioGroupPrimitive.Item
            key={option.value}
            value={option.value}
            className={cn(
              'group flex items-start gap-2.5 rounded-sm px-1.5 py-1.5 text-left transition-colors duration-[180ms] ease-editorial',
              'hover:bg-cream focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand',
            )}
          >
            <span className="mt-0.5 flex size-4 shrink-0 items-center justify-center rounded-full border border-rule bg-card transition-colors duration-[180ms] ease-editorial group-data-[state=checked]:border-brand">
              <RadioGroupPrimitive.Indicator className="size-2 rounded-full bg-brand" />
            </span>
            <span className="min-w-0">
              <span className="block text-sm leading-snug text-ink">{option.label}</span>
              {option.description ? (
                <span className="mt-0.5 block text-xs text-mute">{option.description}</span>
              ) : null}
            </span>
          </RadioGroupPrimitive.Item>
        ),
      )}
    </RadioGroupPrimitive.Root>
  )
}
